import { useMemo } from "react";
import styled from "@emotion/styled";
import { Cell, Pie, PieChart } from "recharts";
import { pulseOpacity } from "styles/animations";
import { Flex } from "styles/utility-components";

import { grey25, grey50, grey100 } from "lib/themes/colors";
import { useBidResponseWorkflow } from "../../../lib/hooks/api/bid/bid_responses/useBidResponseWorkflow";
import { BidDetails } from "../../../lib/hooks/api/bid/bids/useBid";
import { useMinimumLoadingTime } from "../../../lib/hooks/useMinimumLoadingTime";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "../chart/Chart";
import SkeletonText from "../skeleton/SkeletonText";

type BidResponseDonutChartProps = {
  bidResponses: BidDetails["bidResponses"];
};

const LOADING_CHART_DATA = [
  { name: "a", value: 50, color: grey100 },
  { name: "b", value: 20, color: grey50 },
  { name: "c", value: 15, color: grey25 },
];

export default function BidResponseDonutChart({ bidResponses }: BidResponseDonutChartProps) {
  const { data: workflow, isFetching } = useBidResponseWorkflow();
  const isLoading = useMinimumLoadingTime(isFetching, 2000);

  const chartConfig: ChartConfig = useMemo(() => {
    if (!workflow) return {};

    const config: ChartConfig = {};
    workflow.transitions.forEach((transition) => {
      config[transition.stage.id] = {
        label: transition.stage.name,
        // "To do" uses color instead of iconColor as the grey iconColor is too dark for the chart
        color:
          transition.stage.name === "To do" ? transition.stage.color : transition.stage.iconColor,
      };
    });
    return config;
  }, [workflow]);

  // Group bid responses by stage and calculate counts
  const { stageData, chartData } = useMemo(() => {
    if (!workflow) {
      return { stageData: [], chartData: [] };
    }

    // Initialize all stages with count 0
    const stageCounts = new Map<
      string,
      { count: number; stage: (typeof workflow.transitions)[0]["stage"] }
    >();

    workflow.transitions.forEach((transition) => {
      stageCounts.set(transition.stage.id, { count: 0, stage: transition.stage });
    });

    // Count actual responses per stage
    if (bidResponses && bidResponses.length > 0) {
      bidResponses.forEach((response) => {
        const stageId = response.stageId;

        const existing = stageCounts.get(stageId);
        if (existing) {
          existing.count++;
        }
      });
    }

    const stageData = Array.from(stageCounts.values())
      .map((item) => ({
        id: item.stage.id,
        name: item.stage.name,
        count: item.count,
        // "To do" uses color instead of iconColor as the grey iconColor is too dark for the chart
        color: item.stage.name === "To do" ? item.stage.color : item.stage.iconColor,
        order: workflow.transitions.find((t) => t.stage.id === item.stage.id)?.order ?? 0,
      }))
      .sort((a, b) => a.order - b.order);

    const chartData = stageData
      .filter((stage) => stage.count > 0)
      .map((stage) => ({
        name: stage.name,
        value: stage.count,
        color: stage.color,
      }));

    return { stageData, chartData };
  }, [workflow, bidResponses]);

  const displayData = isLoading || !workflow ? LOADING_CHART_DATA : chartData;

  return (
    <OuterContainer>
      <ChartWrapper>
        <MaybePulse isLoading={isLoading || !workflow}>
          <ChartContainer config={chartConfig} aspectRatio="1 / 1">
            <PieChart>
              {!isLoading && workflow && (
                <ChartTooltip content={<ChartTooltipContent hideLabel indicator="dot" />} />
              )}
              <Pie
                data={displayData}
                dataKey="value"
                nameKey="name"
                innerRadius="55%"
                outerRadius="90%"
                strokeWidth={0}
                paddingAngle={0}
              >
                {displayData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
        </MaybePulse>
      </ChartWrapper>
      <Legend>
        {isLoading || !workflow
          ? [80, 70, 60, 50].map((width, i) => (
              <LegendItem key={i}>
                <SkeletonText width={`${width}px`} customSpacing />
              </LegendItem>
            ))
          : stageData.map((stage) => (
              <LegendItem key={stage.id}>
                <LegendColor color={stage.color} />
                <LegendText>{stage.name}</LegendText>
              </LegendItem>
            ))}
      </Legend>
    </OuterContainer>
  );
}

const OuterContainer = styled(Flex)({
  gap: 24,
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  padding: "16px 24px",
});

const ChartWrapper = styled.div({
  flexShrink: 0,
  // Use aspect-ratio to maintain square shape while scaling
  aspectRatio: "1",
  height: "100%",
  minHeight: 120,
});

const Legend = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  flexShrink: 1,
  minWidth: 0,
  maxHeight: "100%",
  overflowY: "auto",
});

const LegendItem = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const LegendColor = styled.div<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  backgroundColor: color,
  borderRadius: 2,
  flexShrink: 0,
}));

const LegendText = styled.div(({ theme }) => ({
  fontSize: 14,
  lineHeight: "20px",
  color: theme.colors.sysTextDefault,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 120,
}));

const MaybePulse = styled.div<{ isLoading: boolean }>(({ isLoading }) => ({
  width: "100%",
  height: "100%",
  ...(isLoading && { animation: `${pulseOpacity} 2s infinite` }),
}));
